import React from "react";
import {
  TextField,
  Button,
  MenuItem,
  Grid,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Paper,
  RadioGroup,
  FormControlLabel,
  Radio
} from "@mui/material";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Person, Badge } from '@mui/icons-material';
import { sharedStyles } from '../styles/sharedStyles';
import { getEnums, idTypes, titles } from '../../utils/enumConfig';

const Step1PersonalInfo = ({ formData, setFormData, errors = {}, setErrors, handleNext }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Get dynamic enums based on selected plan
  const enums = getEnums(formData.recommendedMedicalAidPlan);
  const { maritalStatuses, languages, ethnicities } = enums;

  const handleChange = (e) => {
    // Map UI field names to API-required keys
    const mapping = {
      idType: 'idpasstype',
      idNumber: 'mainid_number',
      title: 'main_title',
      surname: 'main_surname',
      firstName: 'main_firstname',
      initials: 'main_initial',
      maritalStatus: 'main_marital',
      dateOfBirth: 'm_dob',
      language: 'main_lang',
      ethnicity: 'm_race',
      gender: 'mgender_radio',
    };
    const { name, value } = e.target;
    const apiKey = mapping[name] || name;
    setFormData({ ...formData, [apiKey]: value });
    setErrors({ ...errors, [apiKey]: undefined });
  };

  const handleDateChange = (date) => {
    setFormData({ ...formData, m_dob: date ? date.toISOString().split('T')[0] : '' });
    setErrors({ ...errors, m_dob: undefined });
  };

  const validate = () => {
    return true;
  };

  const onNext = (e) => {
    e.preventDefault();
    if (validate()) handleNext();
  };

  return (
    <Box component="form" onSubmit={onNext} autoComplete="off" sx={sharedStyles.formContainer}>
      <Typography variant="h5" sx={sharedStyles.stepTitle}>
        Personal Information
      </Typography>
      <Paper sx={sharedStyles.card}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="ID / Passport"
              name="idType"
              value={formData.idpasstype || ""}
              onChange={handleChange}
              fullWidth
              required
              {...sharedStyles.inputProps}
              error={!!errors.idpasstype}
              helperText={errors.idpasstype}
            >
              {idTypes.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="ID / Passport Number"
              name="idNumber"
              value={formData.mainid_number || ""}
              onChange={handleChange}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Badge color="action" />
                  </InputAdornment>
                ),
                ...(formData.idpasstype === 'ID Document' ? {
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  type: 'tel',
                } : {})
              }}
              {...sharedStyles.inputProps}
              error={!!errors.mainid_number}
              helperText={errors.mainid_number}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
          <TextField
            select
            label="Title"
            name="title"
            value={formData.main_title || ""}
            onChange={handleChange}
            fullWidth
              {...sharedStyles.inputProps}
            error={!!errors.main_title}
            helperText={errors.main_title}
          >
            {titles.map((option) => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </TextField>
        </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Surname"
              name="surname"
              value={formData.main_surname || ""}
              onChange={handleChange}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color="action" />
                  </InputAdornment>
                ),
              }}
              {...sharedStyles.inputProps}
              error={!!errors.main_surname}
              helperText={errors.main_surname}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
          <TextField
            label="First Name"
            name="firstName"
            value={formData.main_firstname || ""}
            onChange={handleChange}
            fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color="action" />
                  </InputAdornment>
                ),
              }}
              {...sharedStyles.inputProps}
            error={!!errors.main_firstname}
            helperText={errors.main_firstname}
          />
        </Grid>
          <Grid item xs={12} sm={6}>
          <TextField
              label="Initials"
              name="initials"
              value={formData.main_initial || ""}
            onChange={handleChange}
            fullWidth
              {...sharedStyles.inputProps}
            error={!!errors.main_initial}
            helperText={errors.main_initial}
          />
        </Grid>
          <Grid item xs={12} sm={6}>
          <TextField
              select
              label="Marital Status"
              name="maritalStatus"
              value={formData.main_marital || ""}
            onChange={handleChange}
            fullWidth
              {...sharedStyles.inputProps}
            error={!!errors.main_marital}
            helperText={errors.main_marital}
            >
              {maritalStatuses.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
        </Grid>
          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Date of Birth"
                value={formData.m_dob ? new Date(formData.m_dob) : null}
                onChange={handleDateChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="m_dob"
                    fullWidth
                    required
                    error={!!errors.m_dob || params.error}
                    helperText={errors.m_dob || params.helperText}
                  />
                )}
              />
            </LocalizationProvider>
            {errors.m_dob && (
              <div style={{ color: 'red', fontSize: 12, marginTop: 4, width: '100%', display: 'block' }}>
                {errors.m_dob}
              </div>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography sx={{ mb: 1, fontWeight: 500 }}>Gender</Typography>
            <RadioGroup
              row
              name="gender"
              value={formData.mgender_radio || ""}
              onChange={handleChange}
            >
              <FormControlLabel value="Male" control={<Radio color="primary" />} label="Male" />
              <FormControlLabel value="Female" control={<Radio color="primary" />} label="Female" />
            </RadioGroup>
            {!!errors.mgender_radio && (
              <Typography color="error" variant="caption">{errors.mgender_radio}</Typography>
            )}
        </Grid>
          <Grid item xs={12} sm={6}>
          <TextField
              select
              label="Language"
              name="language"
              value={formData.main_lang || ""}
            onChange={handleChange}
            fullWidth
              {...sharedStyles.inputProps}
            error={!!errors.main_lang}
            helperText={errors.main_lang}
            >
              {languages.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
        </Grid>
          <Grid item xs={12} sm={6}>
          <TextField
              select
              label="Ethnicity"
              name="ethnicity"
              value={formData.m_race || ""}
            onChange={handleChange}
            fullWidth
              {...sharedStyles.inputProps}
            error={!!errors.m_race}
            helperText={errors.m_race}
            >
              {ethnicities.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
        </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default Step1PersonalInfo; 